import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
  isPriceVariationsEnabled,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ModalInMobile, PrimaryButton, AvatarSmall, H1, H2, IconReviewStar } from '../../components';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';

import css from './OrderPanel.module.css';
import { levelsList } from '../../util/constants';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { matchPathname } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
 const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};


const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig, showTrialBookingPrice, trialLessonPrice } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const trialPrice = (trialLessonPrice?.amount / 100) * 5;
  // Convert money amount to a number and show as credits
  const credits = price ? convertMoneyToNumber(price) : null;
  // If displaying in points/credits, keep one decimal place
  if (credits == null) return null;

  const value = showTrialBookingPrice
    ? trialPrice
    : credits * 5;

  // Remove .0 if it's a whole number
  const formattedCredits =
    Number.isInteger(value) ? value.toString() : value.toFixed(1);

  return (
    <div className={css.price} title={intl.formatMessage({ id: 'ListingCard.credits' }, { credits: formattedCredits })}>
      {hasMultiplePriceVariants ? (
        <span className={css.statLabel}>
          <FormattedMessage id="ListingCard.creditsStartingFrom" values={{ credits: formattedCredits }} />
        </span>
      ) : (
        <span className={css.statLabel}>
          <FormattedMessage id="ListingCard.credits" values={{ credits: formattedCredits }} />
          <span className={css.statLabelSuffix}>
            <FormattedMessage id="ListingCard.creditsLabel" />
          </span>
        </span>
      )}
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const [showTrialBookingPrice, setTrialBooking]=useState(false);
  const routeConfiguration = useRouteConfiguration();
  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    // Inquiry props (bubble down to BookingFixedDurationForm)
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    reviews,
     trialLessonPrice,
    trialLesson20Min,
    isTrialEligible,
    avatarUrl,
    setInsufficientFundsOpen,
     insufficientFundsOpen,
  } = props;

  const currentPage = getResolvedCurrentPage(location, routeConfiguration);
  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', priceVariants, startTimeInterval } =
    publicData || {};

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  // Reviews: compute average rating and total count (public, not deleted)
  const reviewList = Array.isArray(reviews) ? reviews : [];
  const validReviews = reviewList.filter(
    r => r?.attributes?.state === 'public' && !r?.attributes?.deleted && typeof r?.attributes?.rating === 'number'
  );
  const reviewCount = validReviews.length;
  const averageRating = reviewCount
    ? validReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) / reviewCount
    : 0;

  const showPriceMissing = isPaymentProcess && !price;
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled, videoIntroduction, levels=[], sessionTimeMinutes=50 ,
} =
    listing?.attributes?.publicData || {};
  const selectedLevelKey = Array.isArray(levels) ? levels[0] : levels;
  const pageLang = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const levelItem = levelsList?.find(l => l.value === selectedLevelKey || l.key === selectedLevelKey);
  const level = levelItem ? (pageLang === 'ar' ? levelItem.label_ar : levelItem.label_en) : undefined;

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;
  const openInquiryOnLoad = String(searchParams.contactOpen) === 'true';

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
      isPriceVariationsInUse,
      priceVariants,
      priceVariantFieldComponent: PriceVariantPicker,
      preselectedPriceVariant,
      isPublishedListing: isPublishedListing(listing),
    }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
      ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
      : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    trialLessonPrice,
    trialLesson20Min,
    isTrialEligible,
    routeConfiguration,
    history
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');
  const listingTitleString = listing?.attributes?.title || '';

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  // Video controls and overlay
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const overlayHideTimer = useRef(null);

  const clearOverlayTimer = () => {
    if (overlayHideTimer.current) {
      clearTimeout(overlayHideTimer.current);
      overlayHideTimer.current = null;
    }
  };

  const scheduleOverlayHide = (delayMs = 3000) => {
    clearOverlayTimer();
    overlayHideTimer.current = setTimeout(() => setShowOverlay(false), delayMs);
  };
  const handleOverlayToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play();
    } else {
      v.pause();
    }
  };
  const onVideoPlay = () => {
    setIsPlaying(true);
    setShowOverlay(true);
    scheduleOverlayHide(5000);
  };
  const onVideoPause = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };
  const onVideoEnded = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };

  useEffect(() => () => clearOverlayTimer(), []);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          // Defer scroll until after modal has toggled scrolling back on
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              if (document?.documentElement) document.documentElement.scrollTop = 0;
              if (document?.body) document.body.scrollTop = 0;
            }
          }, 0);
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        {/* <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div> */}

        {/* {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )} */}
        {videoIntroduction && currentPage !=='OrderDetailsPage'? (
          <div
            className={css.videoContainer}
            onMouseEnter={() => {
              setShowOverlay(true);
              clearOverlayTimer();
            }}
            onMouseLeave={() => {
              if (isPlaying) scheduleOverlayHide(1500);
            }}
          >
            <video
              ref={videoRef}
              className={css.videoElement}
              src={videoIntroduction}
              controls={false}
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              onContextMenu={(e) => e.preventDefault()}
              onPlay={onVideoPlay}
              onPause={onVideoPause}
              onEnded={onVideoEnded}
            />
            {showOverlay && (
              <button
                type="button"
                className={css.playOverlay}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
                onClick={handleOverlayToggle}
              >
                {isPlaying ? (
                  <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="5" y="4" width="5" height="16" fill="currentColor" rx="1" />
                    <rect x="14" y="4" width="5" height="16" fill="currentColor" rx="1" />
                  </svg>
                ) : (
                  <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : avatarUrl ? (
        <div className={css.noVideo}>
          <img src={avatarUrl} alt="Avatar" />
        </div>
        ) : null
        }
        <div className={css.orderWrapper}>
          <div className={css.orderHead}>
            <div className={css.orderCol}>
              <div className={css.orderColHeading}>
                <FormattedMessage id="OrderPanel.levelHeading" defaultMessage="Level" />
              </div>
              <div className={css.level}>{level}</div>
            </div>
            <div className={css.orderCol}>
              <PriceMaybe
                price={price}
                publicData={publicData}
                validListingTypes={validListingTypes}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                showTrialBookingPrice={showTrialBookingPrice}
                trialLessonPrice={trialLessonPrice}
              />
              <div className={css.sessionLength}>
                <FormattedMessage
                  id="OrderPanel.sessionLength"
                  defaultMessage="{minutes}-min lesson"
                  values={{ minutes: showTrialBookingPrice? 20: sessionTimeMinutes }}
                />
              </div>
            </div>
            <div className={css.statBox}>
              <div className={css.statIconRow}>
                <IconReviewStar isFilled className={css.star} label="rating" />
                <span className={css.averageRating}>{averageRating.toFixed(1)}</span>
              </div>
              <div className={css.statValue}>
                {intl.formatMessage(
                  { id: 'ListingCard.reviewsCount' },
                  { count: reviewCount }
                )}
              </div>
            </div>
          </div>

          {showPriceMissing ? (
            <PriceMissing />
          ) : showInvalidCurrency ? (
            <InvalidCurrency />
          ) : showInvalidPriceVariantsMessage ? (
            <InvalidPriceVariants />
          ) : showBookingFixedDurationForm ? (
            <BookingFixedDurationForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingFixedDurationForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              timeSlotsForDate={timeSlotsForDate}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              startTimeInterval={startTimeInterval}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
              // Inquiry modal props
              listingTitle={listingTitleString}
              authorDisplayName={authorDisplayName}
              onManageDisableScrolling={onManageDisableScrolling}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              openInquiryOnLoad={openInquiryOnLoad}
              setTrialBooking={setTrialBooking}
              showTrialBookingPrice ={showTrialBookingPrice }
              listing={listing}
              insufficientFundsOpen={insufficientFundsOpen}
              setInsufficientFundsOpen={setInsufficientFundsOpen}
            />
          ) : showBookingTimeForm ? (
            <BookingTimeForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              timeSlotsForDate={timeSlotsForDate}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
            />
          ) : showBookingDatesForm ? (
            <BookingDatesForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              {...priceVariantsMaybe}
              {...sharedProps}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              currentStock={currentStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              onContactUser={onContactUser}
              {...sharedProps}
            />
          ) : showInquiryForm ? (
            <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
          ) : !isKnownProcess ? (
            <p className={css.errorSidebar}>
              <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
            </p>
          ) : null}
          {/* <div className={css.responseTime}>
            <div className={css.responseTimeHeading}><IconCard type="clock" /><FormattedMessage id='OrderPanel.responseTimeLabel'/></div>
            <div className={css.responseTimeValue}><FormattedMessage id='OrderPanel.responseTime'/></div>
          </div> */}
        </div>
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            className={css.primaryButtonMobile}
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default OrderPanel;
